// Tiny flow-state engine. Each `<FlowProvider>` instance owns one flow's
// shared context + per-step results, and offers helpers for steps to
// read/write the context, register results, and reset.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ApiResponseSnapshot, RequestSpec } from '@/types';
import type { FlowContext, FlowContextKey, FlowResults } from './types';

interface FlowState {
  flowId: string;
  context: FlowContext;
  /** Set/merge values into the shared context. */
  setContext: (patch: Partial<FlowContext>) => void;
  results: FlowResults;
  /** Mark a step as completed, storing its spec/response and any ids it produced. */
  recordStepResult: (
    stepId: string,
    update: { spec?: RequestSpec; response?: ApiResponseSnapshot; produced?: Partial<FlowContext> }
  ) => void;
  /** Wipe all per-step state (kept for "Restart flow" buttons). */
  reset: () => void;

  // Step navigation (controlled by the host flow component)
  activeStepId: string;
  setActiveStepId: (id: string) => void;

  /** Returns true when every key in `keys` is present in the shared context. */
  hasContext: (...keys: FlowContextKey[]) => boolean;
}

const Ctx = createContext<FlowState | null>(null);

interface ProviderProps {
  flowId: string;
  initialActiveStepId: string;
  /** Optional hydration values (e.g. populate organization_id / workspace_id
   *  from the topbar on first render). */
  initialContext?: Partial<FlowContext>;
  children: ReactNode;
}

export function FlowProvider({
  flowId,
  initialActiveStepId,
  initialContext,
  children,
}: ProviderProps) {
  const [context, setContextState] = useState<FlowContext>(() => ({ ...initialContext }));
  const [results, setResults] = useState<FlowResults>({});
  const [activeStepId, setActiveStepId] = useState<string>(initialActiveStepId);

  const setContext = useCallback((patch: Partial<FlowContext>) => {
    setContextState((prev) => {
      let changed = false;
      const next: FlowContext = { ...prev };
      for (const [k, v] of Object.entries(patch) as [FlowContextKey, FlowContext[FlowContextKey]][]) {
        if (next[k] !== v) {
          next[k] = v as never;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const recordStepResult = useCallback<FlowState['recordStepResult']>(
    (stepId, update) => {
      setResults((prev) => ({
        ...prev,
        [stepId]: {
          ...prev[stepId],
          ...update,
          ranAt: update.response ? Date.now() : prev[stepId]?.ranAt,
        },
      }));
      if (update.produced) setContext(update.produced);
    },
    [setContext]
  );

  const reset = useCallback(() => {
    setResults({});
    setContextState({ ...initialContext });
    setActiveStepId(initialActiveStepId);
  }, [initialActiveStepId, initialContext]);

  const hasContext = useCallback<FlowState['hasContext']>(
    (...keys) => keys.every((k) => Boolean(context[k])),
    [context]
  );

  const value = useMemo<FlowState>(
    () => ({
      flowId,
      context,
      setContext,
      results,
      recordStepResult,
      reset,
      activeStepId,
      setActiveStepId,
      hasContext,
    }),
    [flowId, context, setContext, results, recordStepResult, reset, activeStepId, hasContext]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFlow(): FlowState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useFlow must be called inside <FlowProvider>');
  return v;
}

/** Soft variant — returns null when no flow context is active (so a single
 *  shared component can render in both flow and standalone playgrounds). */
export function useOptionalFlow(): FlowState | null {
  return useContext(Ctx);
}
